import React, { useEffect, useMemo, useState, useCallback } from "react";
import Sidebar from "./Sidebar";
import "./GroupsPage.css";
import {
  createGroup,
  getGroups,
  deleteGroup,
  leaveGroup,
  inviteToGroup,
  getGroupMembers,
  getPendingInvites,
  acceptGroupInvite,
  declineGroupInvite,
  getGroupMemberStats,
  getGroupMemberCalendar,
  getGroupMemberDayReview,
  getGroupComments,
  createGroupComment,
  getMe,
} from "../services/api";

const API_BASE = process.env.REACT_APP_API_BASE || "http://127.0.0.1:5000";

function formatMoney(value) {
  const amount = Number(value || 0);
  if (amount > 0) return `+$${amount.toFixed(2)}`;
  if (amount < 0) return `-$${Math.abs(amount).toFixed(2)}`;
  return "$0.00";
}

function getMonthString(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function getScreenshotSrc(url) {
  if (!url) return null;
  return url.startsWith("http") ? url : `${API_BASE}${url}`;
}

function DayReviewModal({ open, memberName, date, data, comments, commentText, setCommentText, onClose, onSubmitComment, submittingComment }) {
  if (!open) return null;
  return (
    <div className="group-modal-backdrop" onClick={onClose}>
      <div className="group-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="group-modal-header">
          <div>
            <p className="group-modal-label">Shared Reflection</p>
            <h2>{memberName} · {date}</h2>
            <p className="group-modal-subtitle">View reflections, screenshots, and comments for this day.</p>
          </div>
          <button className="close-button" onClick={onClose}>Close</button>
        </div>

        {!data ? (
          <p className="group-empty-state">No shared data found for this day.</p>
        ) : (
          <>
            <div className="group-day-total-card">
              <div className="group-day-total-title">Daily Total</div>
              <div className={`group-day-total-value ${Number(data.daily_total) >= 0 ? "positive" : "negative"}`}>
                {formatMoney(data.daily_total)}
              </div>
              <div className="group-day-total-meta">{data.trade_count} trade(s)</div>
            </div>

            <div className="group-trade-list">
              {(data.trades || []).map((trade) => (
                <div className="group-trade-card" key={trade.id}>
                  <div className="group-trade-profit">{formatMoney(trade.profit)}</div>
                  <div className="group-trade-badges">
                    {trade.session && <span className="group-pill">{trade.session}</span>}
                    {trade.setup_type && <span className="group-pill">{trade.setup_type}</span>}
                    {trade.mood && <span className="group-pill">{trade.mood}</span>}
                  </div>
                  <div className="group-detail-grid">
                    <div className="group-detail-box"><strong>Confluences</strong><p>{trade.confluences || "—"}</p></div>
                    <div className="group-detail-box"><strong>Reason</strong><p>{trade.reason || "—"}</p></div>
                    <div className="group-detail-box"><strong>Improvements</strong><p>{trade.improvements || "—"}</p></div>
                    <div className="group-detail-box"><strong>Notes</strong><p>{trade.notes || "—"}</p></div>
                  </div>
                  {(trade.visual_summary || trade.setup_detected || trade.entry_quality || trade.risk_comment) && (
                    <div className="group-ai-box">
                      <strong>Chart Review</strong>
                      <p><span>Visual Summary:</span> {trade.visual_summary || "—"}</p>
                      <p><span>Setup:</span> {trade.setup_detected || "—"}</p>
                      <p><span>Entry Quality:</span> {trade.entry_quality || "—"}</p>
                      <p><span>Risk Comment:</span> {trade.risk_comment || "—"}</p>
                    </div>
                  )}
                  {trade.screenshot_url && (
                    <img className="group-trade-image" src={getScreenshotSrc(trade.screenshot_url)} alt="Trade screenshot" />
                  )}
                </div>
              ))}
            </div>

            <div className="group-comments-panel">
              <h3>Comments</h3>
              <div className="group-comments-list">
                {comments.length === 0 ? (
                  <p className="group-muted-text">No comments yet. Be the first to leave feedback.</p>
                ) : (
                  comments.map((comment) => (
                    <div className="group-comment-card" key={comment.id}>
                      <div className="group-comment-top">
                        <strong>{comment.author_name || comment.author_email || "Member"}</strong>
                        <span>{comment.created_at_text || "Just now"}</span>
                      </div>
                      <p>{comment.text}</p>
                    </div>
                  ))
                )}
              </div>
              <div className="group-comment-form">
                <textarea
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Leave useful feedback, encouragement, or accountability..."
                />
                <button className="primary-button" onClick={onSubmitComment} disabled={submittingComment}>
                  {submittingComment ? "Posting..." : "Post Comment"}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function MemberCalendar({ days, onDayClick }) {
  const allDays = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const firstDay = new Date(year, month, 1);
    const totalDays = new Date(year, month + 1, 0).getDate();
    const map = {};
    days.forEach((d) => { map[d.date] = d; });
    const items = [];
    for (let i = 1; i <= totalDays; i++) {
      const dateText = `${year}-${String(month + 1).padStart(2, "0")}-${String(i).padStart(2, "0")}`;
      items.push({ date: dateText, day: i, info: map[dateText] || null });
    }
    return { firstDay, items };
  }, [days]);

  const blankCount = allDays.firstDay.getDay();

  return (
    <div className="group-calendar-wrap">
      <div className="group-calendar-headings">
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => <div key={d}>{d}</div>)}
      </div>
      <div className="group-calendar-grid">
        {Array.from({ length: blankCount }).map((_, i) => (
          <div key={`blank-${i}`} className="group-calendar-cell blank" />
        ))}
        {allDays.items.map((item) => {
          let colorClass = "neutral";
          if (item.info && Number(item.info.total) > 0) colorClass = "green";
          if (item.info && Number(item.info.total) < 0) colorClass = "red";
          return (
            <button key={item.date} className={`group-calendar-cell ${colorClass}`} onClick={() => onDayClick(item.date)}>
              <div className="group-calendar-day">{item.day}</div>
              {item.info ? (
                <>
                  <div className="group-calendar-amount">{formatMoney(item.info.total)}</div>
                  <div className="group-calendar-meta">{item.info.trade_count} trade(s)</div>
                </>
              ) : (
                <div className="group-calendar-meta muted">No trades</div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function GroupsPage() {
  const [groups, setGroups] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberStats, setMemberStats] = useState(null);
  const [memberCalendar, setMemberCalendar] = useState([]);
  const [currentDate] = useState(new Date());
  const [userPlan, setUserPlan] = useState("starter");
  const [currentUserId, setCurrentUserId] = useState("");

  const [groupName, setGroupName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [decliningId, setDecliningId] = useState("");
  const [deletingGroupId, setDeletingGroupId] = useState("");
  const [leavingGroupId, setLeavingGroupId] = useState("");

  const [dayModalOpen, setDayModalOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState("");
  const [selectedDayData, setSelectedDayData] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [postingComment, setPostingComment] = useState(false);
  const [error, setError] = useState("");

  const handleSelectMember = useCallback(async (groupId, member) => {
    try {
      setSelectedMember(member);
      setError("");
      const month = getMonthString(currentDate);
      const [statsData, calendarData] = await Promise.all([
        getGroupMemberStats(groupId, member.uid),
        getGroupMemberCalendar(groupId, member.uid, month),
      ]);
      setMemberStats(statsData);
      setMemberCalendar(calendarData.days || []);
    } catch (err) {
      setError(err.message || "Failed to load member view");
    }
  }, [currentDate]);

  const handleSelectGroup = useCallback(async (group) => {
    try {
      setSelectedGroup(group);
      setSelectedMember(null);
      setMemberStats(null);
      setMemberCalendar([]);
      setError("");
      const data = await getGroupMembers(group.id);
      setMembers(data.members || []);
      if ((data.members || []).length > 0) {
        handleSelectMember(group.id, data.members[0]);
      }
    } catch (err) {
      setError(err.message || "Failed to load members");
    }
  }, [handleSelectMember]);

  const loadInitial = useCallback(async () => {
    try {
      setError("");
      const [groupsData, invitesData, meData] = await Promise.all([
        getGroups(), getPendingInvites(), getMe(),
      ]);
      setGroups(groupsData.groups || []);
      setPendingInvites(invitesData.invites || []);
      setUserPlan(meData.plan || "starter");
      setCurrentUserId(meData.uid || "");
      if ((groupsData.groups || []).length > 0) {
        handleSelectGroup(groupsData.groups[0]);
      }
    } catch (err) {
      setError(err.message || "Failed to load groups");
    }
  }, [handleSelectGroup]);

  useEffect(() => { loadInitial(); }, [loadInitial]);

  async function handleCreateGroup() {
    if (!groupName.trim()) return;
    try {
      setCreatingGroup(true);
      setError("");
      await createGroup(groupName.trim());
      setGroupName("");
      await loadInitial();
    } catch (err) {
      setError(err.message || "Failed to create group");
    } finally {
      setCreatingGroup(false);
    }
  }

  async function handleDeleteGroup(groupId) {
    if (!window.confirm("Delete this group? This cannot be undone.")) return;
    try {
      setDeletingGroupId(groupId);
      setError("");
      await deleteGroup(groupId);
      if (selectedGroup?.id === groupId) setSelectedGroup(null);
      await loadInitial();
    } catch (err) {
      setError(err.message || "Failed to delete group");
    } finally {
      setDeletingGroupId("");
    }
  }

  async function handleLeaveGroup(groupId) {
    if (!window.confirm("Leave this group?")) return;
    try {
      setLeavingGroupId(groupId);
      setError("");
      await leaveGroup(groupId);
      if (selectedGroup?.id === groupId) setSelectedGroup(null);
      await loadInitial();
    } catch (err) {
      setError(err.message || "Failed to leave group");
    } finally {
      setLeavingGroupId("");
    }
  }

  async function handleAcceptInvite(groupId) {
    try {
      setError("");
      await acceptGroupInvite(groupId);
      await loadInitial();
    } catch (err) {
      setError(err.message || "Failed to accept invite");
    }
  }

  async function handleDeclineInvite(groupId, inviteId) {
    try {
      setDecliningId(inviteId);
      setError("");
      await declineGroupInvite(groupId);
      setPendingInvites((prev) => prev.filter((i) => i.id !== inviteId));
    } catch (err) {
      setError(err.message || "Failed to decline invite");
    } finally {
      setDecliningId("");
    }
  }

  async function handleInvite() {
    if (!selectedGroup || !inviteEmail.trim()) return;
    try {
      setSendingInvite(true);
      setError("");
      await inviteToGroup(selectedGroup.id, inviteEmail.trim());
      setInviteEmail("");
      await handleSelectGroup(selectedGroup);
    } catch (err) {
      setError(err.message || "Failed to send invite");
    } finally {
      setSendingInvite(false);
    }
  }

  async function handleDayClick(date) {
    if (!selectedGroup || !selectedMember) return;
    if (userPlan !== "pro") {
      setError("Viewing member day reviews requires Pro.");
      return;
    }
    try {
      setSelectedDay(date);
      setDayModalOpen(true);
      setSelectedDayData(null);
      setComments([]);
      setError("");
      const [reviewData, commentsData] = await Promise.all([
        getGroupMemberDayReview(selectedGroup.id, selectedMember.uid, date),
        getGroupComments(selectedGroup.id, selectedMember.uid, date),
      ]);
      setSelectedDayData(reviewData);
      setComments(commentsData.comments || []);
    } catch (err) {
      setError(err.message || "Failed to load shared day review");
    }
  }

  async function handleSubmitComment() {
    if (!commentText.trim() || !selectedGroup || !selectedMember || !selectedDay) return;
    try {
      setPostingComment(true);
      await createGroupComment(selectedGroup.id, {
        target_uid: selectedMember.uid,
        target_date: selectedDay,
        text: commentText.trim(),
      });
      const commentsData = await getGroupComments(selectedGroup.id, selectedMember.uid, selectedDay);
      setComments(commentsData.comments || []);
      setCommentText("");
    } catch (err) {
      setError(err.message || "Failed to post comment");
    } finally {
      setPostingComment(false);
    }
  }

  const isPro = userPlan === "pro";

  return (
    <div className="app-shell">
      <Sidebar />
      <main className="main-content-area">
        <div className="page-header">
          <div>
            <h1 className="page-title">Groups</h1>
            <p className="page-subtitle">
              Private accountability circles where you can review trusted members' stats, calendars, and reflections.
            </p>
          </div>
        </div>

        {error && <div className="error-banner">{error}</div>}

        {pendingInvites.length > 0 && (
          <div className="groups-top-panel">
            <div className="groups-panel-title">
              Pending Invites
              <span className="groups-invite-badge">{pendingInvites.length}</span>
            </div>
            <div className="groups-invite-list">
              {pendingInvites.map((invite) => (
                <div className="groups-invite-card" key={invite.id}>
                  <div>
                    <div className="groups-invite-name">{invite.group_name}</div>
                    <div className="groups-invite-meta">Invited by {invite.invited_by_email || "a member"}</div>
                  </div>
                  <div className="groups-invite-actions">
                    <button className="primary-button" onClick={() => handleAcceptInvite(invite.group_id)}>Join</button>
                    <button
                      className="groups-decline-btn"
                      onClick={() => handleDeclineInvite(invite.group_id, invite.id)}
                      disabled={decliningId === invite.id}
                    >
                      {decliningId === invite.id ? "Declining..." : "Decline"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="groups-layout">
          <section className="groups-sidebar-panel">
            <div className="groups-panel-card">
              <div className="groups-panel-title">Create Group</div>
              <input
                className="groups-input"
                placeholder="Ex: New York Accountability"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
              />
              <button className="primary-button" onClick={handleCreateGroup} disabled={creatingGroup}>
                {creatingGroup ? "Creating..." : "Create Group"}
              </button>
            </div>

            <div className="groups-panel-card">
              <div className="groups-panel-title">Your Groups</div>
              <div className="groups-list">
                {groups.length === 0 ? (
                  <p className="group-muted-text">No groups yet. Create one to get started.</p>
                ) : (
                  groups.map((group) => (
                    <div key={group.id} className="groups-list-item-row">
                      <button
                        className={`groups-list-item ${selectedGroup?.id === group.id ? "active" : ""}`}
                        onClick={() => handleSelectGroup(group)}
                      >
                        <div className="groups-list-name">{group.name}</div>
                        <div className="groups-list-meta">{group.member_count || 0} / 5 members</div>
                      </button>

                      {group.created_by === currentUserId ? (
                        <button
                          className="groups-delete-btn"
                          onClick={() => handleDeleteGroup(group.id)}
                          disabled={deletingGroupId === group.id}
                          title="Delete group"
                        >
                          {deletingGroupId === group.id ? "..." : "✕"}
                        </button>
                      ) : (
                        <button
                          className="groups-leave-btn"
                          onClick={() => handleLeaveGroup(group.id)}
                          disabled={leavingGroupId === group.id}
                          title="Leave group"
                        >
                          {leavingGroupId === group.id ? "..." : "Leave"}
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            {selectedGroup && (
              <div className="groups-panel-card">
                <div className="groups-panel-title">Invite Member</div>
                <input
                  className="groups-input"
                  placeholder="friend@email.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                />
                <button className="primary-button" onClick={handleInvite} disabled={sendingInvite}>
                  {sendingInvite ? "Sending..." : "Send Invite"}
                </button>
              </div>
            )}
          </section>

          <section className="groups-main-panel">
            {!selectedGroup ? (
              <div className="groups-empty-main">
                <h2>Select or create a group</h2>
                <p>Once members join, you'll be able to view their calendars, stats, reflections, and comments.</p>
              </div>
            ) : (
              <>
                <div className="groups-header-card">
                  <div>
                    <div className="groups-kicker">Selected Group</div>
                    <h2>{selectedGroup.name}</h2>
                    <p>{members.length} member(s) in this accountability circle.</p>
                  </div>
                </div>

                <div className="groups-members-card">
                  <div className="groups-panel-title">Members</div>
                  <div className="groups-members-row">
                    {members.map((member) => (
                      <button
                        key={member.uid}
                        className={`groups-member-pill ${selectedMember?.uid === member.uid ? "active" : ""}`}
                        onClick={() => handleSelectMember(selectedGroup.id, member)}
                      >
                        <span>{member.display_name || member.email || "Member"}</span>
                        {member.plan === "pro" && <span className="groups-pro-badge">PRO</span>}
                      </button>
                    ))}
                  </div>
                </div>

                {selectedMember && (
                  <>
                    <div className="groups-member-summary">
                      <div className="groups-member-card">
                        <div className="groups-panel-title">Viewing Member</div>
                        <div className="groups-member-name-row">
                          <h3>{selectedMember.display_name || selectedMember.email || "Member"}</h3>
                          {selectedMember.plan === "pro" && <span className="groups-pro-badge large">PRO</span>}
                        </div>
                        <p className="group-muted-text">Shared stats, calendar, and day-by-day reflections.</p>
                      </div>

                      <div className="groups-stat-card">
                        <div className="groups-stat-label">Total PNL</div>
                        <div className={`groups-stat-value ${Number(memberStats?.total_profit || 0) >= 0 ? "positive" : "negative"}`}>
                          {formatMoney(memberStats?.total_profit || 0)}
                        </div>
                      </div>

                      <div className="groups-stat-card">
                        <div className="groups-stat-label">Win Rate</div>
                        <div className="groups-stat-value neutral">
                          {Number(memberStats?.win_rate || 0).toFixed(0)}%
                        </div>
                      </div>

                      <div className="groups-stat-card">
                        <div className="groups-stat-label">Trades</div>
                        <div className="groups-stat-value neutral">{memberStats?.total_trades || 0}</div>
                      </div>
                    </div>

                    <div className="groups-calendar-card">
                      <div className="groups-panel-title">
                        {selectedMember.display_name || selectedMember.email}'s Calendar
                      </div>
                      {isPro ? (
                        <MemberCalendar days={memberCalendar} onDayClick={handleDayClick} />
                      ) : (
                        <div className="groups-pro-gate">
                          <div className="groups-pro-gate-icon">🔒</div>
                          <h3>Pro Feature</h3>
                          <p>Upgrade to Pro to view member calendars, day-by-day reflections, and leave comments.</p>
                          <button className="primary-button" onClick={() => window.location.href = "/settings"}>
                            Upgrade to Pro
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </section>
        </div>

        <DayReviewModal
          open={dayModalOpen}
          memberName={selectedMember?.display_name || selectedMember?.email || "Member"}
          date={selectedDay}
          data={selectedDayData}
          comments={comments}
          commentText={commentText}
          setCommentText={setCommentText}
          onClose={() => setDayModalOpen(false)}
          onSubmitComment={handleSubmitComment}
          submittingComment={postingComment}
        />
      </main>
    </div>
  );
}

export default GroupsPage;